import { DocIdParams } from '../dto/documents.js';
import { listOf } from '../dto/list.js';
import { ActorSheetV1, ReferenceEntryV1, RulesCreateV1, RulesRecordV1, RulesUpdateV1, SheetCreateV1, SheetIdParams, SheetParams, SheetUpdateV1, VariantCreatedV1, VariantCreateRequestV1, } from '../dto/reference.js';
import { SheetListQueryV1 } from '../dto/sheets-character.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_CARD_CREATE, EXAMPLE_CARD_CREATED, EXAMPLE_CARD_UPDATE, EXAMPLE_CARD_UPDATED, EXAMPLE_REFERENCE_ENTRY, EXAMPLE_RULE_CARD, EXAMPLE_SHEET, EXAMPLE_SHEET_CREATED, EXAMPLE_STATBLOCK, EXAMPLE_VARIANT_CREATED, } from './examples-reference.js';
/**
 * The 5e routes of exit 1b (1.0b deliverable 19): reference pages, cards and sheets read (1.AA),
 * sheets written and the variant flow (1.AC-1). An SRD row's id is 1.AA's; the examples that name
 * one name its `EXAMPLE_IDS` entry (marked `srd`), live in the `demo` template since CS r3a, so
 * none declares a success skip. A stale `baseVersion` answers 409 `row_conflict`.
 */
const ids = EXAMPLE_IDS;
const exit1b = { auth: 'session', status: 'frozen', since: 'exit-1b' };
const reader = { ...exit1b, owner: '1.AA' };
const writer = { ...exit1b, owner: '1.AC-1' };
/**
 * The sheet writes' and the variant flow's refusals (CS r2c, 1.AC-1's item AC2; DECISIONS 146 item
 * 11): a trashed document or home answers 409 `document_trashed`; a type without `has_sheet`, an
 * unknown or archived type, a source that is not a creature, `data.rules` `2024`, or `isPrimary:
 * false` on the primary sheet 422 `invalid_body` with `details.path` and `details.issue`.
 */
const sheetWrite = { 409: ['document_trashed'], 422: ['invalid_body'] };
/** A reference page: its document, entry, source and rules. */
export const referenceGet = defineRoute({
    id: 'reference.get',
    method: 'GET',
    path: '/api/reference/:id',
    params: DocIdParams,
    response: { kind: 'json', schema: ReferenceEntryV1 },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...reader,
    examples: {
        request: { params: { id: ids.referenceGhoul.id } },
        response: EXAMPLE_REFERENCE_ENTRY,
    },
});
/** A spell's or item's card (`EditorServices.getRuleCard`, the card embed). */
export const rulesGet = defineRoute({
    id: 'rules.get',
    method: 'GET',
    path: '/api/docs/:id/rules',
    params: DocIdParams,
    response: { kind: 'json', schema: RulesRecordV1 },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...reader,
    examples: {
        request: { params: { id: ids.referenceSpeakWithDead.id } },
        response: EXAMPLE_RULE_CARD,
    },
});
/**
 * One sheet by its id (`EditorServices.getSheet`, the stat block embed). A character sheet answers
 * 409 `wrong_kind` with `details.kind` (CS r6a, answered from 2.I s2a): it is
 * `sheets.character.get`'s.
 */
export const sheetsGet = defineRoute({
    id: 'sheets.get',
    method: 'GET',
    path: '/api/sheets/:sheetId',
    params: SheetIdParams,
    response: { kind: 'json', schema: ActorSheetV1 },
    errors: { 409: ['wrong_kind'] },
    reference: 'read',
    ...reader,
    examples: {
        request: { params: { sheetId: ids.sheetDrownedAcolyte.id } },
        response: EXAMPLE_SHEET,
    },
});
/**
 * One document's stat block sheets (a bounded list), the primary first. `kind` takes `statblock`
 * only, also the default (CS r6a, open point 44): a document's character sheets are
 * `sheets.character.list`'s, so the answer stays `ActorSheetV1`.
 */
export const sheetsList = defineRoute({
    id: 'sheets.list',
    method: 'GET',
    path: '/api/docs/:id/sheets',
    params: DocIdParams,
    query: SheetListQueryV1,
    response: { kind: 'json', schema: listOf(ActorSheetV1) },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    ...writer,
    examples: {
        request: { params: { id: ids.creatureDrownedAcolyte.id }, query: {} },
        response: { items: [EXAMPLE_SHEET], next: null },
    },
});
export const sheetsCreate = defineRoute({
    id: 'sheets.create',
    method: 'POST',
    path: '/api/docs/:id/sheets',
    params: DocIdParams,
    body: SheetCreateV1,
    response: { kind: 'json', schema: ActorSheetV1, status: 201 },
    errors: sheetWrite,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...writer,
    examples: {
        request: {
            params: { id: ids.npcOrlaVenn.id },
            body: {
                label: 'Harbour watch captain',
                // No feature: a feature's local id is no row of the demo, and a request names rows only.
                data: { ...EXAMPLE_STATBLOCK, name: 'Captain Orla Venn', traits: [], actions: [] },
            },
        },
        response: EXAMPLE_SHEET_CREATED,
    },
});
export const sheetsUpdate = defineRoute({
    id: 'sheets.update',
    method: 'PUT',
    path: '/api/docs/:id/sheets/:sheetId',
    params: SheetParams,
    body: SheetUpdateV1,
    response: { kind: 'json', schema: ActorSheetV1 },
    // CS r6a: a character sheet answers 409 `wrong_kind` (from 2.I s2a); it is
    // `sheets.character.update`'s.
    errors: { 409: ['row_conflict', 'document_trashed', 'wrong_kind'], 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...writer,
    examples: {
        request: {
            params: { id: ids.creatureDrownedAcolyte.id, sheetId: ids.sheetDrownedAcolyte.id },
            body: { baseVersion: 1, label: 'Drowned Acolyte of the Chapel' },
        },
        response: { ...EXAMPLE_SHEET, version: 2, label: 'Drowned Acolyte of the Chapel' },
        consumes: true,
    },
});
/**
 * The variant flow (A2.1 section 4): a canon copy homed where the GM says. From a creature it is a
 * copy of a type with `has_sheet` and its new sheet; from a spell or an item (CS r6c deliverable
 * 202, answered from 2.M s1) a copy of the canon type with `has_card` of that kind, its card the
 * source's, `sheetId` null (`EXAMPLE_VARIANT_SPELL`). A card source's 422 `details.issue` is one of
 * `CARD_WRITE_ISSUES`.
 */
export const variantsCreate = defineRoute({
    id: 'variants.create',
    method: 'POST',
    path: '/api/reference/:id/variants',
    params: DocIdParams,
    body: VariantCreateRequestV1,
    response: { kind: 'json', schema: VariantCreatedV1, status: 201 },
    errors: sheetWrite,
    reference: 'target-ok',
    docIds: { owner: ['body.homeId'], target: ['params.id'] },
    ...writer,
    examples: {
        request: {
            params: { id: ids.referenceGhoul.id },
            body: {
                title: 'Drowned Ghoul',
                typeId: ids.typeSeaBeast.id,
                homeId: ids.locationSunkenChapelCrypt.id,
            },
        },
        response: EXAMPLE_VARIANT_CREATED,
        // It creates a document and its sheet: each run needs a fresh clone (DECISIONS 296 ruling 37).
        consumes: true,
    },
});
/**
 * The card writes of Phase 2c (CS r6c deliverable 202; A2.1 section 4, "Homebrew authoring"),
 * owner 2.M, stage `s1`, since `exit-2c`, frozen; each a change batch (r5b is on feature; DECISIONS
 * 220 ruling 52). A page whose type lacks `has_card`, data or a kind other than the type's,
 * `rules: '2024'` or data neither card schema parses answer 422 `invalid_body` with `details`
 * `{path, issue}` (`CARD_WRITE_ISSUES`); a trashed page 409 `document_trashed`; a second card or a
 * stale `baseRv` 409 `row_conflict` (`details.current.rv` on a stale write). Appended after the
 * Foundry routes, last in the table.
 */
const cards2c = {
    auth: 'session',
    status: 'frozen',
    since: 'exit-2c',
    owner: '2.M',
    stage: 's1',
};
const cardWrite = { 409: ['row_conflict', 'document_trashed'], 422: ['invalid_body'] };
/** A canon page's first card, of its type's kind (`EditorServices.getRuleCard` reads it back). */
export const rulesCreate = defineRoute({
    id: 'rules.create',
    method: 'POST',
    path: '/api/docs/:id/rules',
    params: DocIdParams,
    body: RulesCreateV1,
    response: { kind: 'json', schema: RulesRecordV1, status: 201 },
    errors: cardWrite,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...cards2c,
    changeBatch: true,
    examples: {
        request: { params: { id: ids.itemLanternOfWarding.id }, body: EXAMPLE_CARD_CREATE },
        response: EXAMPLE_CARD_CREATED,
        // It writes the page's one card: each run needs a fresh clone.
        consumes: true,
    },
});
/** A card's new data at the `rv` the GM edited. */
export const rulesUpdate = defineRoute({
    id: 'rules.update',
    method: 'PUT',
    path: '/api/docs/:id/rules',
    params: DocIdParams,
    body: RulesUpdateV1,
    response: { kind: 'json', schema: RulesRecordV1 },
    errors: cardWrite,
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...cards2c,
    changeBatch: true,
    examples: {
        request: { params: { id: ids.spellBrineWard.id }, body: EXAMPLE_CARD_UPDATE },
        response: EXAMPLE_CARD_UPDATED,
        // Brine Ward and its card are CS r6b's `demo@p2c` rows, which the template holds.
        consumes: true,
    },
});
//# sourceMappingURL=reference.js.map